/**
 * The public shell.
 *
 * Kept quiet on purpose. The navigation is a thin band, the search bar is the
 * only prominent control, and everything else defers to the data below it —
 * this is a reference tool, and a reference tool should not compete with its
 * own content for attention.
 */

import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { SiteRail } from '@/components/layout/SiteRail'
import { SearchBar } from '@/components/search/SearchBar'
import {
  BuildingIcon, ChartIcon, CloseIcon, Logo, MapPinIcon, MenuIcon, MoonIcon,
  PackageIcon, ScaleIcon, SourceIcon, SunIcon,
} from '@/components/ui/icons'
import { Button, cx, LinkButton } from '@/components/ui/primitives'
import { useAuth } from '@/stores/auth'
import { useTheme } from '@/stores/theme'

const NAV = [
  { to: '/search', label: 'Businesses', icon: BuildingIcon },
  { to: '/products', label: 'Products & prices', icon: PackageIcon },
  { to: '/markets', label: 'Market data', icon: ChartIcon },
  // Both of these are server-rendered by Flask. They are in the app's nav
  // anyway, because the two surfaces serve one site and a link that exists in
  // one header and not the other makes them look like two.
  { to: '/locations/nigeria', label: 'Locations', icon: MapPinIcon },
  { to: '/reports', label: 'Reports', icon: SourceIcon },
]

export function PublicLayout() {
  const location = useLocation()
  const { resolved, cycle } = useTheme()
  const { isAuthenticated, isStaff, user } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)

  // A route change must close the mobile menu, or the user navigates and the
  // overlay stays on top of where they just arrived.
  useEffect(() => setMenuOpen(false), [location.pathname])

  // The homepage carries its own large search field; repeating it in the header
  // would be two search boxes on one screen.
  const showHeaderSearch = location.pathname !== '/'
  // The same test the header search uses: the homepage is the one page
  // that leads with its own search and wants nothing beside it.
  const isHome = location.pathname === '/'

  return (
    <div className="flex min-h-screen flex-col bg-[var(--surface-page)]">
      {/* Skip link — the first tab stop, invisible until focused. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-[var(--accent)] focus:px-4 focus:py-2 focus:text-sm focus:text-white"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-40 border-b border-[var(--border-subtle)] bg-[var(--surface-page)]/85 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-4 px-4 sm:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <Logo size={26} />
            <span className="text-[0.95rem] font-semibold tracking-tight text-[var(--text-primary)]">
              Naija<span className="text-[var(--accent-text)]">Index</span>
            </span>
          </Link>

          {showHeaderSearch ? (
            <SearchBar className="hidden max-w-md flex-1 md:block" />
          ) : (
            <div className="flex-1" />
          )}

          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cx(
                    'rounded-md px-3 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'bg-[var(--surface-hover)] text-[var(--text-primary)] font-medium'
                      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={cycle}
              aria-label={`Switch to ${resolved === 'dark' ? 'light' : 'dark'} theme`}
              className="px-2"
            >
              {resolved === 'dark' ? <SunIcon size={16} /> : <MoonIcon size={16} />}
            </Button>

            {/* Signed out, the header shows nothing here. This is a public
                reference tool; the people who need the admin surface know
                where it is, and a sign-in button on every page invites
                everyone else to try the door. The path is VITE_LOGIN_PATH. */}
            {isAuthenticated ? (
              <>
                {isStaff ? (
                  <LinkButton to="/admin" variant="secondary" size="sm" className="hidden sm:inline-flex">
                    Admin
                  </LinkButton>
                ) : null}
                <Link
                  to="/account"
                  className="hidden size-8 items-center justify-center rounded-full bg-[var(--accent-subtle)] text-xs font-semibold text-[var(--accent-text)] sm:flex"
                  title={user?.full_name}
                >
                  {user?.first_name?.[0]}
                  {user?.last_name?.[0]}
                </Link>
              </>
            ) : null}

            <Button
              variant="ghost"
              size="sm"
              className="px-2 lg:hidden"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label="Menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <CloseIcon size={18} /> : <MenuIcon size={18} />}
            </Button>
          </div>
        </div>

        {menuOpen ? (
          <div className="border-t border-[var(--border-subtle)] bg-[var(--surface-panel)] px-4 py-3 lg:hidden">
            <SearchBar className="mb-3 md:hidden" />
            <nav className="space-y-0.5">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cx(
                      'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm',
                      isActive
                        ? 'bg-[var(--surface-hover)] font-medium text-[var(--text-primary)]'
                        : 'text-[var(--text-secondary)]',
                    )
                  }
                >
                  <item.icon size={16} />
                  {item.label}
                </NavLink>
              ))}
              <div className="mt-2 border-t border-[var(--border-subtle)] pt-2">
                {isAuthenticated ? (
                  <>
                    {isStaff ? (
                      <Link to="/admin" className="block rounded-md px-3 py-2 text-sm text-[var(--text-secondary)]">
                        Admin
                      </Link>
                    ) : null}
                    <Link to="/account" className="block rounded-md px-3 py-2 text-sm text-[var(--text-secondary)]">
                      Account
                    </Link>
                  </>
                ) : null}
              </div>
            </nav>
          </div>
        ) : null}
      </header>

      {/* The rail sits in the layout rather than in each page, so a new route
          gets it by existing rather than by its author remembering. The
          homepage opts out: its job is to get a visitor into the search box,
          and a column of market cards beside it competes for exactly the
          attention the page is asking for.

          Below `lg` the rail is hidden outright rather than stacked under the
          content. On a phone it would be four cards a reader has to scroll past
          to reach the footer, and everything in it is reachable from the nav. */}
      <main id="main" className="flex-1">
        {isHome ? (
          <Outlet />
        ) : (
          <div className="mx-auto grid grid-cols-1 w-full max-w-[92rem] lg:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="min-w-0">
              <Outlet />
            </div>
            <aside
              aria-label="Index summary"
              className="hidden px-4 py-10 sm:px-6 lg:block"
            >
              <SiteRail />
            </aside>
          </div>
        )}
      </main>

      <Footer />
    </div>
  )
}

function Footer() {
  return (
    <footer className="mt-16 border-t border-[var(--border-subtle)] bg-[var(--surface-panel)]">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-2">
              <Logo size={22} />
              <span className="text-sm font-semibold text-[var(--text-primary)]">NaijaIndex</span>
            </div>
            <p className="mt-2.5 max-w-xs text-xs leading-relaxed text-[var(--text-secondary)]">
              Business intelligence and commercial data infrastructure for Nigeria.
              Every figure traces back to a source and a date.
            </p>
          </div>

          <FooterColumn
            title="Explore"
            links={[
              { to: '/search', label: 'Businesses' },
              { to: '/products', label: 'Products & prices' },
              { to: '/markets', label: 'Market data' },
              { to: '/states', label: 'By state' },
            ]}
          />
          <FooterColumn
            title="Understand"
            links={[
              { to: '/methodology', label: 'How we source data' },
              { to: '/methodology#scores', label: 'Quality & corroboration' },
              { to: '/methodology#corrections', label: 'Report a correction' },
            ]}
          />

          <div>
            <p className="field-label">Coverage</p>
            <ul className="mt-2.5 space-y-1.5 text-xs text-[var(--text-secondary)]">
              <li className="flex items-center gap-1.5">
                <ScaleIcon size={12} /> 36 states and the FCT
              </li>
              <li className="flex items-center gap-1.5">
                <PackageIcon size={12} /> 774 local government areas
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-2 border-t border-[var(--border-subtle)] pt-6 text-2xs text-[var(--text-tertiary)] sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} NaijaIndex. Data is provided as observed, with sources.</p>
          {/* An intelligence product should say plainly what it is not. */}
          <p>
            Listings are observations, not endorsements. Verify before you transact.
          </p>
        </div>
      </div>
    </footer>
  )
}

function FooterColumn({
  title, links,
}: {
  title: string
  links: Array<{ to: string; label: string }>
}) {
  return (
    <div>
      <p className="field-label">{title}</p>
      <ul className="mt-2.5 space-y-1.5">
        {links.map((link) => (
          <li key={link.to}>
            <Link
              to={link.to}
              className="text-xs text-[var(--text-secondary)] transition-colors hover:text-[var(--accent-text)]"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
